import React from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import "./crearProyectoPublico.css";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";
import { PROYECTOS_SOLICITUD_CAMPO_LABELS } from "../../config/proyectosSolicitudCamposLabels";
import PortalSolicitanteTrazabilidad, {
  type PortalDocItem,
  type PortalPaso,
} from "../../components/Proyectos/PortalSolicitanteTrazabilidad";

type DocHabilitado = {
  tipo: string;
  etiqueta: string;
  etapa_key: string;
  extensiones: string[];
};

export default function CorreccionSolicitudPublicaPage() {
  const [codigo, setCodigo] = React.useState("");
  const [documento, setDocumento] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);
  const [detalle, setDetalle] = React.useState<any>(null);
  const [campos, setCampos] = React.useState<Record<string, any>>({});
  const [puedeCorregirCampos, setPuedeCorregirCampos] = React.useState(false);
  const [puedeSubirDocumentos, setPuedeSubirDocumentos] = React.useState(false);
  const [archivosPorTipo, setArchivosPorTipo] = React.useState<Record<string, File | null>>({});
  const [subiendoTipo, setSubiendoTipo] = React.useState<string | null>(null);
  const [modalInstruccionAbierto, setModalInstruccionAbierto] = React.useState(false);
  const [retieCantidades, setRetieCantidades] = React.useState({
    transformadores: 0,
    medidores: 0,
    tc: 0,
    tp: 0,
  });
  const [guardandoRetieCant, setGuardandoRetieCant] = React.useState(false);
  const pasoRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const docRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const seccionDatosRef = React.useRef<HTMLDivElement | null>(null);
  const backendBase = getProyectosApiBaseUrl();

  const pasosTrazabilidad: PortalPaso[] = React.useMemo(
    () => (Array.isArray(detalle?.trazabilidad?.pasos) ? detalle.trazabilidad.pasos : []),
    [detalle],
  );

  const instruccionModal = detalle?.instruccion_modal;
  const itemsModal = React.useMemo(() => {
    if (instruccionModal?.accion === "corregir_datos" && Array.isArray(detalle?.editable_fields)) {
      return detalle.editable_fields.map(
        (f: string) => PROYECTOS_SOLICITUD_CAMPO_LABELS[f] || f,
      );
    }
    return Array.isArray(instruccionModal?.items) ? instruccionModal.items : [];
  }, [instruccionModal, detalle?.editable_fields]);

  const fetchCorreccionApi = async (path: string, init?: RequestInit) => {
    return fetch(`${backendBase}${path}`, init);
  };

  const aplicarRespuestaAcceso = (data: any) => {
    setDetalle(data);
    setCampos(data.campos || {});
    setPuedeCorregirCampos(Boolean(data.puede_corregir_campos));
    setPuedeSubirDocumentos(Boolean(data.puede_subir_documentos));
    setArchivosPorTipo({});
    if (data.retie_portal?.cantidades) {
      const c = data.retie_portal.cantidades;
      setRetieCantidades({
        transformadores: Number(c.transformadores || 0),
        medidores: Number(c.medidores || 0),
        tc: Number(c.tc || 0),
        tp: Number(c.tp || 0),
      });
    }
    if (data.instruccion_modal?.mostrar) {
      setModalInstruccionAbierto(true);
    }
  };

  const buscar = async () => {
    try {
      setLoading(true);
      setError(null);
      setOk(null);
      const res = await fetchCorreccionApi(`/publico/solicitud-correccion/acceso`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo, documento }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No fue posible validar la solicitud.");
      aplicarRespuestaAcceso(data);
    } catch (e: any) {
      setError(e.message || "Error consultando solicitud.");
      setDetalle(null);
    } finally {
      setLoading(false);
    }
  };

  const scrollA = (el: HTMLElement | null) => {
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const irSegunInstruccion = () => {
    setModalInstruccionAbierto(false);
    const accion = instruccionModal?.accion;
    if (accion === "corregir_datos") {
      scrollA(seccionDatosRef.current);
      return;
    }
    if (accion === "ir_cargar") {
      const docTipo = instruccionModal?.documento_tipo;
      const pasoId = instruccionModal?.paso_id;
      if (docTipo && docRefs.current[docTipo]) {
        scrollA(docRefs.current[docTipo]);
      } else if (pasoId && pasoRefs.current[pasoId]) {
        scrollA(pasoRefs.current[pasoId]);
      } else {
        const activo = pasosTrazabilidad.find((p) => p.estado_paso === "activo");
        scrollA(pasoRefs.current[activo?.id || ""] || null);
      }
      const trazaBox = document.getElementById("portal-trazabilidad-tabs");
      scrollA(trazaBox);
    }
  };

  const guardar = async () => {
    if (!puedeCorregirCampos) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetchCorreccionApi(`/publico/solicitud-correccion/${detalle?.proyecto?.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo, documento, campos }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No fue posible guardar correcciones.");
      setOk("Correcciones enviadas correctamente. Tu solicitud queda en revisión.");
      await buscar();
    } catch (e: any) {
      setError(e.message || "Error guardando correcciones.");
    } finally {
      setLoading(false);
    }
  };

  const subirDocumento = async (doc: PortalDocItem | DocHabilitado) => {
    if (!detalle?.proyecto?.id) {
      setError("Primero valida el proyecto con código y cédula.");
      return;
    }
    const archivo = archivosPorTipo[doc.tipo];
    if (!archivo) {
      setError(`Selecciona un archivo para: ${doc.etiqueta}.`);
      return;
    }
    try {
      setSubiendoTipo(doc.tipo);
      setError(null);
      setOk(null);
      const form = new FormData();
      form.append("codigo", codigo);
      form.append("documento", documento);
      form.append("archivo", archivo);
      form.append("etapa_key", doc.etapa_key);

      const res = await fetchCorreccionApi(
        `/publico/solicitud-correccion/${detalle.proyecto.id}/documentos/${encodeURIComponent(doc.tipo)}`,
        { method: "POST", body: form },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No fue posible subir el documento.");
      const msgOk = `${doc.etiqueta}: archivo cargado correctamente.`;
      setArchivosPorTipo((prev) => ({ ...prev, [doc.tipo]: null }));
      const resAcceso = await fetchCorreccionApi(`/publico/solicitud-correccion/acceso`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo, documento }),
      });
      const dataAcceso = await resAcceso.json();
      if (resAcceso.ok) {
        aplicarRespuestaAcceso(dataAcceso);
      }
      setOk(msgOk);
    } catch (e: any) {
      setError(e.message || "Error subiendo documento.");
    } finally {
      setSubiendoTipo(null);
    }
  };

  const hayCamposEditables = puedeCorregirCampos && Object.keys(campos).length > 0;
  const hayTrazabilidad = pasosTrazabilidad.length > 0;
  const retiePortal = detalle?.retie_portal;
  const hayRetieCantidades = Boolean(retiePortal?.pendiente_cantidades);

  const declararCantidadesRetie = async () => {
    if (!detalle?.proyecto?.id) return;
    const total =
      retieCantidades.transformadores +
      retieCantidades.medidores +
      retieCantidades.tc +
      retieCantidades.tp;
    if (total <= 0) {
      setError("Indique al menos un equipo (transformador, medidor, TC o TP).");
      return;
    }
    try {
      setGuardandoRetieCant(true);
      setError(null);
      setOk(null);
      const res = await fetchCorreccionApi(
        `/publico/solicitud-correccion/${detalle.proyecto.id}/retie/cantidades`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ codigo, documento, cantidades: retieCantidades }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No fue posible registrar las cantidades.");
      setOk(data.mensaje || "Cantidades registradas. Ya puede cargar los documentos RETIE.");
      await buscar();
    } catch (e: any) {
      setError(e.message || "Error al registrar cantidades RETIE.");
    } finally {
      setGuardandoRetieCant(false);
    }
  };

  return (
    <Box className="tramites-public-page">
      <Box className="tramites-public-topbar">
        <Container maxWidth="lg" className="tramites-public-topbar-inner">
          <Box className="tramites-public-gov-wrap">
            <img src="/images/logo2.webp" alt="GOV.CO" className="tramites-public-gov-logo" />
          </Box>
          <Typography className="tramites-public-lang">ES</Typography>
        </Container>
      </Box>

      <Box className="tramites-public-logobar">
        <Container maxWidth="lg" className="tramites-public-logobar-inner">
          <Box className="tramites-public-logo-block">
            <img src="/images/logo-emcali.webp" alt="EMCALI" className="tramites-public-logo" />
          </Box>
          <a href="/proyectos/crear" className="tramites-public-back-btn">
            Volver
          </a>
        </Container>
      </Box>

      <Box className="tramites-public-main">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography className="tramites-public-title">Portal del solicitante</Typography>
          <Typography className="tramites-public-subtitle">
            Consulte el avance de su trámite por pasos. Solo podrá cargar lo que EMCALI autorice en cada etapa; los
            documentos ya enviados quedan bloqueados hasta nueva indicación del revisor.
          </Typography>
          <Paper className="tramites-public-card" sx={{ p: 2, borderRadius: 3 }}>
            <Stack spacing={1.5}>
              {error && <Alert severity="error">{error}</Alert>}
              {ok && <Alert severity="success">{ok}</Alert>}
              <TextField label="Código del proyecto" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
              <TextField
                label="Número de cédula del solicitante"
                value={documento}
                onChange={(e) => setDocumento(e.target.value)}
              />
              <Button variant="contained" onClick={buscar} disabled={loading}>
                Validar y continuar
              </Button>
            </Stack>
          </Paper>

          {detalle?.proyecto && (
            <Paper className="tramites-public-card" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {detalle.proyecto.nombre}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Código: <strong>{detalle.proyecto.codigo}</strong>
              </Typography>

              {String(detalle.situacion_portal || detalle.trazabilidad?.resumen || "").trim() ? (
                <Alert
                  severity={detalle.tiene_accion_pendiente ? "warning" : "info"}
                  sx={{ mb: 1.5 }}
                  action={
                    detalle.tiene_accion_pendiente ? (
                      <Button color="inherit" size="small" onClick={() => setModalInstruccionAbierto(true)}>
                        Ver instrucciones
                      </Button>
                    ) : undefined
                  }
                >
                  {detalle.situacion_portal || detalle.trazabilidad?.resumen}
                </Alert>
              ) : null}

              {String(detalle.observaciones || "").trim() ? (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Observaciones del revisor
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {detalle.observaciones}
                  </Typography>
                </Alert>
              ) : null}

              {hayRetieCantidades && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                    Documentación RETIE — declarar equipos
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Indique cuántos transformadores, medidores, TC y TP debe cargar. No hay límite en las cantidades.
                    {retiePortal?.interventor_nombre
                      ? ` Interventor asignado: ${retiePortal.interventor_nombre}.`
                      : ""}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
                    <TextField
                      label="Transformadores"
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      value={retieCantidades.transformadores}
                      onChange={(e) =>
                        setRetieCantidades((prev) => ({
                          ...prev,
                          transformadores: Math.max(0, Number(e.target.value) || 0),
                        }))
                      }
                    />
                    <TextField
                      label="Medidores"
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      value={retieCantidades.medidores}
                      onChange={(e) =>
                        setRetieCantidades((prev) => ({
                          ...prev,
                          medidores: Math.max(0, Number(e.target.value) || 0),
                        }))
                      }
                    />
                    <TextField
                      label="TC"
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      value={retieCantidades.tc}
                      onChange={(e) =>
                        setRetieCantidades((prev) => ({
                          ...prev,
                          tc: Math.max(0, Number(e.target.value) || 0),
                        }))
                      }
                    />
                    <TextField
                      label="TP"
                      type="number"
                      size="small"
                      inputProps={{ min: 0 }}
                      value={retieCantidades.tp}
                      onChange={(e) =>
                        setRetieCantidades((prev) => ({
                          ...prev,
                          tp: Math.max(0, Number(e.target.value) || 0),
                        }))
                      }
                    />
                  </Stack>
                  <Button variant="contained" onClick={declararCantidadesRetie} disabled={guardandoRetieCant}>
                    {guardandoRetieCant ? "Guardando…" : "Confirmar cantidades y habilitar cargas"}
                  </Button>
                </Paper>
              )}

              {(hayTrazabilidad || hayCamposEditables) && (
                <Box id="portal-trazabilidad-tabs">
                  <PortalSolicitanteTrazabilidad
                    pasos={pasosTrazabilidad}
                    pestanasApi={detalle.trazabilidad?.pestanas}
                    pasoActivoId={detalle.trazabilidad?.paso_activo_id || null}
                    pestanaActivaId={detalle.trazabilidad?.pestana_activa_id || null}
                    archivosPorTipo={archivosPorTipo}
                    subiendoTipo={subiendoTipo}
                    onArchivoChange={(tipo, file) =>
                      setArchivosPorTipo((prev) => ({ ...prev, [tipo]: file }))
                    }
                    onSubir={subirDocumento}
                    pasoRefs={pasoRefs}
                    docRefs={docRefs}
                    correccionDatos={
                      hayCamposEditables
                        ? {
                            campos,
                            labels: PROYECTOS_SOLICITUD_CAMPO_LABELS,
                            onCampoChange: (key, value) =>
                              setCampos((prev) => ({ ...prev, [key]: value })),
                            onGuardar: guardar,
                            guardando: loading,
                            seccionRef: seccionDatosRef,
                          }
                        : null
                    }
                  />
                </Box>
              )}

              {!hayCamposEditables && !puedeSubirDocumentos && !hayTrazabilidad && !hayRetieCantidades && (
                <Alert severity="info">
                  No hay información de trámite disponible. Verifique el código y la cédula o contacte a EMCALI.
                </Alert>
              )}
            </Paper>
          )}
        </Container>
      </Box>

      <Dialog
        open={modalInstruccionAbierto}
        onClose={() => setModalInstruccionAbierto(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{instruccionModal?.titulo || "Instrucciones"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: "pre-wrap" }}>
            {instruccionModal?.mensaje}
          </Typography>
          {itemsModal.length > 0 ? (
            <List dense disablePadding>
              {itemsModal.map((item: string, i: number) => (
                <ListItem key={i} disableGutters>
                  <ListItemText primary={`• ${item}`} />
                </ListItem>
              ))}
            </List>
          ) : null}
          {instruccionModal?.accion === "ir_cargar" ? (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Los documentos ya cargados no pueden volver a subirse. Solo cargue los que aparecen como pendientes en el
              paso activo.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setModalInstruccionAbierto(false)}>Cerrar</Button>
          {instruccionModal?.accion === "corregir_datos" || instruccionModal?.accion === "ir_cargar" ? (
            <Button variant="contained" onClick={irSegunInstruccion}>
              {instruccionModal?.accion === "corregir_datos" ? "Ir a corregir datos" : "Ir a cargar documentos"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Box className="tramites-public-footer">
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box className="tramites-public-footer-grid">
            <Box>
              <img
                src="/images/LOGO-EMCALI-vertical-1-tinta-blanca.webp"
                alt="EMCALI"
                className="tramites-public-footer-brand"
              />
            </Box>
            <Box>
              <Typography className="tramites-public-footer-title">Sede Electrónica</Typography>
              <Typography className="tramites-public-footer-text">
                Av 2N entre Calles 10 y 11, CAM Torre EMCALI.
              </Typography>
              <Typography className="tramites-public-footer-text">
                Horario: Lunes a viernes 8:00AM a 5:00PM.
              </Typography>
              <Typography className="tramites-public-footer-text">
                Linea de atencion: 177 - 018000 4 12017
              </Typography>
            </Box>
            <Box>
              <Typography className="tramites-public-footer-title">Información</Typography>
              <Typography className="tramites-public-footer-text">Terminos y condiciones</Typography>
              <Typography className="tramites-public-footer-text">Proteccion de datos personales</Typography>
              <Typography className="tramites-public-footer-text">Mapa del sitio</Typography>
            </Box>
            <Box className="tramites-public-footer-seals">
              <img src="/images/image 7.png" alt="Colombia" className="tramites-public-seal" />
              <img src="/images/image 8.png" alt="Icontec" className="tramites-public-seal" />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
